/**
 * 워크플로우 API
 */

import { api } from './client';
import type { WorkflowStatus } from '../../types/api.types';

export interface WorkflowRunResponse {
  workflow_id: string;
  status: string;
  message: string;
}

export interface WorkflowListResponse {
  items: Array<{
    workflow_id: string;
    status: string;
    started_at: string;
    completed_at?: string;
    total_items_processed: number;
    progress: number;
  }>;
  total: number;
  page: number;
  size: number;
  pages: number;
  has_next?: boolean;
  has_prev?: boolean;
}

export interface WorkflowLogsResponse {
  workflow_id: string;
  logs: Array<{
    timestamp: string;
    level: string;
    message: string;
  }>;
}

export const workflowApi = {
  /**
   * 워크플로우 파이프라인 실행
   */
  run: async (): Promise<WorkflowRunResponse> => {
    return api.post<WorkflowRunResponse>('/api/v1/workflows/run');
  },

  /**
   * 실행 중/완료 워크플로우 목록 조회
   */
  list: async (params?: {
    page?: number;
    size?: number;
    status?: string;
    sort_by?: string;
    sort_order?: string;
  }): Promise<WorkflowListResponse> => {
    return api.get<WorkflowListResponse>('/api/v1/workflows', params);
  },

  /**
   * 스케줄러 시작
   */
  startScheduler: async (config?: { interval_seconds?: number; sources?: string[]; keyword?: string }): Promise<void> => {
    await api.post('/api/v1/scheduler/start', config);
  },

  /**
   * 스케줄러 중지
   */
  stopScheduler: async (): Promise<void> => {
    await api.post('/api/v1/scheduler/stop');
  },

  /**
   * 스케줄러 상태 조회
   */
  getSchedulerStatus: async (): Promise<{ scheduler_running: boolean; job_count: number; interval_seconds: number }> => {
    const response = await api.get<{ scheduler_running: boolean; job_count: number; interval_seconds: number }>('/api/v1/scheduler/status');
    return response;
  },

  /**
   * 콘텐츠 워크플로우 상태 조회
   */
  getWorkflowStatus: async (contentId: number): Promise<WorkflowStatus> => {
    const response = await api.get<WorkflowStatus>(`/api/v1/content-v5/${contentId}/workflow`);
    return response;
  },

  /**
   * 백그라운드 워크플로우 실행 상태 조회
   */
  getRunStatus: async (workflowId: string): Promise<WorkflowStatus> => {
    return api.get<WorkflowStatus>(`/api/v1/workflows/${workflowId}`);
  },

  /**
   * 백그라운드 워크플로우 로그 조회
   */
  getRunLogs: async (workflowId: string, params?: { limit?: number; level?: string }): Promise<WorkflowLogsResponse> => {
    return api.get<WorkflowLogsResponse>(`/api/v1/workflows/${workflowId}/logs`, params);
  },

  /**
   * 백그라운드 워크플로우 취소
   */
  cancelRun: async (workflowId: string): Promise<{ workflow_id: string; status: string }> => {
    return api.post<{ workflow_id: string; status: string }>(`/api/v1/workflows/${workflowId}/cancel`);
  },
};
